/**
 * Stateless per-chunk SSE parser.
 * Parses the current chunk only and returns a single event as JSON.
 * No buffering or event accumulation across chunks.
 *
 * @param chunk - One chunk string (e.g. "event: stream_start\ndata: [START]")
 * @returns Parsed event as object, or null if chunk is incomplete/invalid
 */
export function parseSeekChunk(chunk: string): Record<string, unknown> | null {
  if (!chunk || typeof chunk !== 'string') {
    return null;
  }

  const lines = chunk.split('\n').map((line) => line.trim());
  let eventType: string | null = null;
  let dataContent: string | null = null;

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventType = line.substring(6).trim();
    } else if (line.startsWith('data:')) {
      dataContent = line.substring(5).trim();
    }
  }

  if (!eventType || dataContent === null || dataContent === '') {
    return null;
  }

  switch (eventType) {
    case 'stream_start':
    case 'stream_end':
      return { type: eventType, data: dataContent };

    case 'token': {
      const colonSpaceIndex = dataContent.indexOf(': ');
      if (colonSpaceIndex === -1) {
        return { type: 'token', agent: '', content: dataContent };
      }
      const agent = dataContent.substring(0, colonSpaceIndex);
      const content = dataContent.substring(colonSpaceIndex + 2);
      return { type: 'token', agent, content };
    }

    case 'function_call':
    case 'function_response':
    case 'final': {
      try {
        const parsed = JSON.parse(dataContent) as Record<string, unknown>;
        return { type: eventType, ...parsed };
      } catch {
        return { type: eventType, raw: dataContent, parseError: true };
      }
    }

    default:
      return { type: eventType, data: dataContent };
  }
}
